import React, { useEffect, useState } from "react";
import { Col, Row, Typography, Radio, Button, notification, Input } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    inquiry_type: "",
    request_message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "Please input your first name!";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Please input your last name!";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Please input your email!";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email!";
    }

    if (!formData.inquiry_type) {
      newErrors.inquiry_type = "Please select an inquiry type!";
    }

    if (!formData.request_message.trim()) {
      newErrors.request_message = "Please enter your message!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!navigator.onLine) {
      notification.error({
        message: "No Internet Connection",
        description: "Please check your internet connection and try again.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_TRANSPORT_SUPPORT}`,
        formData
      );

      if (response.status === 200) {
        notification.success({
          message: "Success",
          description: "Message sent successfully",
        });
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          inquiry_type: "",
          request_message: "",
        });
        // Reset the form fields after successful submission
        setErrors({});
      } else if (response.status === 400) {
        notification.error({
          message: "Error",
          description: "Please fill in all required fields correctly.",
        });
      } else if (response.status === 500) {
        notification.error({
          message: "Error",
          description: "Internal server error. Please try again later.",
        });
      } else {
        notification.error({
          message: "Error",
          description: "Unexpected response status",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Unable to send a support at this time. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        backgroundColor: "white",
        padding: "2rem",
        borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <Title level={2} style={{ color: "#08839B", marginBottom: "1rem" }}>
          Contact Support
        </Title>
        <Text
          style={{
            color: "#666",
            fontSize: "16px",
            display: "block",
          }}
        >
          Having trouble? We're here to help! Fill out the form below and we'll
          respond as soon as possible.
        </Text>
      </div>

      <div>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                First Name *
              </label>
              <Input
                size="large"
                placeholder="Enter your first name"
                value={formData.first_name}
                onChange={(e) =>
                  handleInputChange("first_name", e.target.value)
                }
                status={errors.first_name ? "error" : ""}
                style={{ borderRadius: "8px" }}
              />
              {errors.first_name && (
                <Text
                  type="danger"
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {errors.first_name}
                </Text>
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Last Name *
              </label>
              <Input
                size="large"
                placeholder="Enter your last name"
                value={formData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                status={errors.last_name ? "error" : ""}
                style={{ borderRadius: "8px" }}
              />
              {errors.last_name && (
                <Text
                  type="danger"
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {errors.last_name}
                </Text>
              )}
            </div>
          </Col>
        </Row>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Email Address *
          </label>
          <Input
            size="large"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            status={errors.email ? "error" : ""}
            style={{ borderRadius: "8px" }}
          />
          {errors.email && (
            <Text
              type="danger"
              style={{ fontSize: "12px", marginTop: "4px", display: "block" }}
            >
              {errors.email}
            </Text>
          )}
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "1rem",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Issue Category *
          </label>
          <Radio.Group
            value={formData.inquiry_type}
            onChange={(e) => handleInputChange("inquiry_type", e.target.value)}
            style={{ width: "100%" }}
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12}>
                <Radio
                  value="account_opening"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Account Opening
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="account_deletion"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Account Deletion
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="technical_issues"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Technical Issues
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="password_reset"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Password Reset
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="feature_request"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Feature Request
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="bug_report"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  Bug Report
                </Radio>
              </Col>
              <Col xs={24} sm={12}>
                <Radio
                  value="general_inquiry"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 0",
                  }}
                >
                  General Inquiry
                </Radio>
              </Col>
            </Row>
          </Radio.Group>
          {errors.inquiry_type && (
            <Text
              type="danger"
              style={{ fontSize: "12px", marginTop: "8px", display: "block" }}
            >
              {errors.inquiry_type}
            </Text>
          )}
        </div>

        <div style={{ marginBottom: "2rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Message *
          </label>
          <Input.TextArea
            rows={5}
            placeholder="Please describe your issue or question in detail. The more information you provide, the better we can assist you."
            showCount
            maxLength={1000}
            value={formData.request_message}
            onChange={(e) =>
              handleInputChange("request_message", e.target.value)
            }
            status={errors.request_message ? "error" : ""}
            style={{ borderRadius: "8px", fontSize: "14px" }}
          />
          {errors.request_message && (
            <Text
              type="danger"
              style={{ fontSize: "12px", marginTop: "4px", display: "block" }}
            >
              {errors.request_message}
            </Text>
          )}
        </div>

        <div style={{ textAlign: "center" }}>
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            loading={loading}
            style={{
              backgroundColor: "#048181",
              borderColor: "#048181",
              fontSize: "16px",
              fontWeight: "600",
              height: "50px",
              width: "100%",
              maxWidth: "300px",
              borderRadius: "25px",
              boxShadow: "0 4px 12px rgba(4, 129, 129, 0.3)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#036b6b";
              e.target.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#048181";
              e.target.style.transform = "translateY(0)";
            }}
          >
            {loading ? "Sending Message..." : "Send Message"}
          </Button>
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: "2rem",
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <Text style={{ color: "#666", fontSize: "14px" }}>
            📧 We typically respond within 24 hours during business days.
            <br />
            For urgent issues, please include "URGENT" in your message.
          </Text>
        </div>
      </div>
    </div>
  );
};

function YouthplusSupport() {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        background: "linear-gradient(135deg, #048181 0%, #764ba2 100%)",
        padding: "4rem 1rem",
      }}
    >
      <div
        style={{
          textAlign: "center",
          marginBottom: "3rem",
          color: "white",
        }}
      >
        <Title
          level={1}
          style={{
            color: "white",
            marginBottom: "1rem",
            fontSize: "3rem",
            fontWeight: "700",
          }}
        >
          <QuestionCircleOutlined style={{ marginRight: "1rem" }} />
          Support Center
        </Title>
        <Text
          style={{
            color: "rgba(255,255,255,0.9)",
            fontSize: "18px",
            display: "block",
            maxWidth: "600px",
            margin: "0 auto",
            lineHeight: "1.6",
          }}
        >
          Need help? Our support team is here to assist you. Send us a message
          and we'll get back to you as quickly as possible.
        </Text>
      </div>

      <ContactForm />

      <div
        style={{
          textAlign: "center",
          marginTop: "3rem",
          color: "rgba(255,255,255,0.8)",
        }}
      >
        <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>
          © {new Date().getFullYear()} Support Center. We're committed to
          providing excellent customer service.
        </Text>
      </div>
    </div>
  );
}

export default YouthplusSupport;
